import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../common/Sidebar';
import { linkAPI } from '../../services/apiClient';
import { toast } from 'react-hot-toast';
import { useBusiness } from '../../services/BusinessContext';
import styles from './SurveyLinks.module.css';

const SurveyLinks = () => {
    const { surveyId } = useParams();
    const navigate = useNavigate();
    const { business, loading: businessLoading } = useBusiness();
    
    const [links, setLinks] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [qrCodes, setQrCodes] = useState({});
    
    // Form state
    const [formData, setFormData] = useState({
        start_date: '',
        end_date: '',
        max_responses: ''
    });

    const fetchLinks = useCallback(async () => {
        try {
            setIsLoading(true);
            const response = await linkAPI.listLinks(surveyId);
            setLinks(response.data || []);
        } catch (error) {
            console.error('Error fetching links:', error);
            toast.error('Failed to load survey links');
        } finally {
            setIsLoading(false);
        }
    }, [surveyId]);

    useEffect(() => {
        fetchLinks();
    }, [fetchLinks]);

    const handleCreateLink = async (e) => {
        e.preventDefault();
        try {
            const linkData = {};
            if (formData.start_date) linkData.start_date = formData.start_date;
            if (formData.end_date) linkData.end_date = formData.end_date;
            if (formData.max_responses) linkData.max_responses = parseInt(formData.max_responses, 10);
            
            await linkAPI.createLink(surveyId, linkData);
            toast.success('Survey link created successfully!');
            setShowCreateForm(false);
            setFormData({ start_date: '', end_date: '', max_responses: '' });
            fetchLinks();
        } catch (error) {
            console.error('Error creating link:', error);
            toast.error(error.response?.data?.error || 'Failed to create link');
        }
    };

    const handleDownloadQR = async (linkId) => {
        try {
            // Check if QR is already loaded
            if (qrCodes[linkId]) {
                downloadBlob(qrCodes[linkId], `survey-qr-${linkId}.png`);
                return;
            }
            
            const response = await linkAPI.getQRCode(surveyId, linkId);
            const blob = response.data;
            setQrCodes(prev => ({ ...prev, [linkId]: blob }));
            downloadBlob(blob, `survey-qr-${linkId}.png`);
            toast.success('QR Code downloaded!');
        } catch (error) {
            console.error('Error downloading QR code:', error);
            toast.error('Failed to download QR code');
        }
    };

    const downloadBlob = (blob, filename) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    const handleViewQR = async (linkId) => {
        try {
            if (!qrCodes[linkId]) {
                const response = await linkAPI.getQRCode(surveyId, linkId);
                setQrCodes(prev => ({ ...prev, [linkId]: response.data }));
            }
        } catch (error) {
            console.error('Error loading QR code:', error);
            toast.error('Failed to load QR code');
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Link copied to clipboard!');
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'No limit';
        return new Date(dateStr).toLocaleDateString();
    };

    const getShareableUrl = (link) => {
        const baseUrl = window.location.origin;
        return `${baseUrl}/survey/${link.code}`;
    };

    if (businessLoading || isLoading) {
        return (
            <div className={styles.pageContainer}>
                <Sidebar />
                <div className={styles.mainContent}>
                    <p>Loading survey links...</p>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.pageContainer}>
            <Sidebar />
            <div className={styles.mainContent}>
                <div className={styles.header}>
                    <h1 className={styles.title}>Survey Links</h1>
                    <p className={styles.subtitle}>
                        Manage shareable links for your survey
                    </p>
                    <div className={styles.headerActions}>
                        <button 
                            className={styles.backButton}
                            onClick={() => navigate(-1)}
                        >
                            ← Back
                        </button>
                        <button 
                            className={styles.createButton}
                            onClick={() => setShowCreateForm(!showCreateForm)}
                        >
                            {showCreateForm ? 'Cancel' : '+ Create New Link'}
                        </button>
                    </div>
                </div>

                {/* Create Link Form */}
                {showCreateForm && (
                    <div className={styles.createForm}>
                        <h3>Create New Survey Link</h3>
                        <form onSubmit={handleCreateLink}>
                            <div className={styles.formRow}>
                                <div className={styles.formGroup}>
                                    <label htmlFor="start_date">Start Date (Optional)</label>
                                    <input
                                        type="datetime-local"
                                        id="start_date"
                                        value={formData.start_date}
                                        onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                                    />
                                </div>
                                <div className={styles.formGroup}>
                                    <label htmlFor="end_date">End Date (Optional)</label>
                                    <input
                                        type="datetime-local"
                                        id="end_date"
                                        value={formData.end_date}
                                        onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                                    />
                                </div>
                                <div className={styles.formGroup}>
                                    <label htmlFor="max_responses">Max Responses (Optional)</label>
                                    <input
                                        type="number"
                                        id="max_responses"
                                        min="1"
                                        value={formData.max_responses}
                                        onChange={(e) => setFormData(prev => ({ ...prev, max_responses: e.target.value }))}
                                        placeholder="Unlimited"
                                    />
                                </div>
                            </div>
                            <button type="submit" className={styles.submitButton}>
                                Create Link
                            </button>
                        </form>
                    </div>
                )}

                {/* Links List */}
                <div className={styles.linksContainer}>
                    {links.length === 0 ? (
                        <div className={styles.emptyState}>
                            <p>No links created yet. Create a link to share your survey!</p>
                        </div>
                    ) : (
                        <div className={styles.linksList}>
                            {links.map((link) => (
                                <div key={link.id} className={styles.linkCard}>
                                    <div className={styles.linkHeader}>
                                        <span className={styles.linkCode}>Code: {link.code}</span>
                                        <span className={`${styles.linkStatus} ${link.is_active ? styles.active : styles.inactive}`}>
                                            {link.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                    </div>
                                    
                                    <div className={styles.linkDetails}>
                                        <div className={styles.detailRow}>
                                            <span className={styles.label}>Shareable URL:</span>
                                            <div className={styles.urlContainer}>
                                                <input 
                                                    type="text" 
                                                    readOnly 
                                                    value={getShareableUrl(link)} 
                                                    className={styles.urlInput}
                                                />
                                                <button 
                                                    className={styles.copyButton}
                                                    onClick={() => copyToClipboard(getShareableUrl(link))}
                                                >
                                                    Copy
                                                </button>
                                            </div>
                                        </div>
                                        
                                        <div className={styles.detailsGrid}>
                                            <div className={styles.detailItem}>
                                                <span className={styles.label}>Start Date:</span>
                                                <span>{formatDate(link.start_date)}</span>
                                            </div>
                                            <div className={styles.detailItem}>
                                                <span className={styles.label}>End Date:</span>
                                                <span>{formatDate(link.end_date)}</span>
                                            </div>
                                            <div className={styles.detailItem}>
                                                <span className={styles.label}>Max Responses:</span>
                                                <span>{link.max_responses || 'Unlimited'}</span>
                                            </div>
                                            <div className={styles.detailItem}>
                                                <span className={styles.label}>Responses:</span>
                                                <span>{link.response_count || 0}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className={styles.qrSection}>
                                        <button 
                                            className={styles.qrButton}
                                            onClick={() => handleViewQR(link.id)}
                                        >
                                            View QR Code
                                        </button>
                                        <button 
                                            className={styles.downloadButton}
                                            onClick={() => handleDownloadQR(link.id)}
                                        >
                                            Download QR
                                        </button>
                                        
                                        {qrCodes[link.id] && (
                                            <div className={styles.qrPreview}>
                                                <img 
                                                    src={URL.createObjectURL(qrCodes[link.id])} 
                                                    alt="QR Code" 
                                                    className={styles.qrImage}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SurveyLinks;
